import React, { useCallback, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { useLoader } from "../../../context/LoaderContext";
import utils from "../../../utils/utils";
import {
  getEffectivePermissionKeys,
  getAllPermissionKeys,
  isSystemAdministratorRole,
} from "../../../utils/permissionUtils";
import { filterAssignablePermissionKeys } from "../../../constants/permissions";
import { expandStoredIsfPermissionKeys } from "../../../constants/isfPermissionConsolidation";
import { expandStoredQmsPermissionKeys } from "../qms/qmsPermissionUtils";
import {
  validateNationalMobile,
  normalizeNationalMobileInput,
  orgMobileDialLabel,
} from "../../../utils/siteFieldValidationUtils";
import { extractSuccessStory } from "../../../services/dmsMasterService";
import {
  fetchOrganizationSetup,
  parseOrganizationSetupResponse,
} from "../../../services/organizationSetupService";
import Dropdown from "../../common/dropDown";
import GenderSelector from "../../common/GenderSelector";
import SimpleCircularUpload from "../../common/SimpleCircularUpload";
import "./userManagement.scss";

const padUniqueCodeNumber = (value, length = 5) => {
  if (value == null || value === "") return "";
  return String(value).padStart(length, "0");
};

const normalizeUniqueCodePurpose = (row = {}) =>
  String(row?.Perpose ?? row?.Purpose ?? row?.perpose ?? "")
    .trim()
    .toLowerCase();

/** Next employee number from EmpNum list (never leave blank / 00000 on Add). */
const applyUniqueCodesFromEmpNum = (empNumList = []) => {
  const list = Array.isArray(empNumList) ? empNumList : [];
  const emp =
    list.find((item) => normalizeUniqueCodePurpose(item) === "employee") ||
    list.find((item) =>
      normalizeUniqueCodePurpose(item).includes("employee"),
    );
  const asc =
    list.find((item) => normalizeUniqueCodePurpose(item) === "ascension") ||
    list.find((item) =>
      normalizeUniqueCodePurpose(item).includes("ascension"),
    );
  const paddedEmp = padUniqueCodeNumber(emp?.NumberFormate, 5);
  const employeeNumber =
    paddedEmp && paddedEmp !== "00000" ? paddedEmp : "00001";
  return {
    prefix: emp?.Prefix || "EMP",
    employeeNumber,
    employeeUniqueId: emp?.UniqueCodeId || "",
    ascNum: padUniqueCodeNumber(asc?.NumberFormate, 5),
    ascNumId: asc?.UniqueCodeId || "",
  };
};

function normalizeRoleLabel(label) {
  return String(label ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function isSponsorOrCroRole(roleLabel) {
  const n = normalizeRoleLabel(roleLabel);
  return (
    n === "sponsor" ||
    n === "cro" ||
    n === "sponsor/cro" ||
    n === "sponsor cro"
  );
}

function isRoleOptionHiddenForUserType(roleLabel, isExternal) {
  const n = normalizeRoleLabel(roleLabel);
  if (n === "super admin") return true;
  const sponsorCroRole = isSponsorOrCroRole(roleLabel);
  if (isExternal) {
    if (n === "system administrator" || n === "principal investigator") {
      return true;
    }
    return !sponsorCroRole;
  }
  if (sponsorCroRole) return true;
  return false;
}

const emptyForm = (orgCountryIso2 = "GB") => ({
  StaffId: "",
  firstName: "",
  lastName: "",
  contactNumber: "",
  mobileCountryIso2: orgCountryIso2 || "GB",
  profilePicture: "",
  prefix: "EMP",
  employeeNumber: "00001",
  employeeUniqueId: "",
  ascNum: "",
  ascNumId: "",
  email: "",
  confirmEmail: "",
  dob: new Date(),
  startDate: new Date(),
  accessDate: new Date(),
  gender: "",
  role: "",
  department: "",
  lineManager: "",
  haveLineManagerRights: false,
  addressLine1: "",
  postalCode: "",
  city: "",
  county: "",
  country: "",
  userPermissions: [],
  qmsTrainingAdditionalRoles: [],
  IsCRO: 1,
});

const formatDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
};

const getFileExtensionFromBase64 = (base64String) => {
  if (!base64String) return "";
  const match = base64String.match(/data:image\/(\w+);base64,/);
  return match ? match[1] : "";
};

/**
 * Add Sponsor/CRO user — single-page form (user info + address).
 * Study assignment stays on the Sponsor/CRO management screen after create.
 */
export default function SponsorCroUserAddModal({
  open,
  onClose,
  onSaved,
  userData,
  showAlert,
}) {
  const { showLoader, hideLoader } = useLoader();
  const [formData, setFormData] = useState(() => emptyForm());
  const [fieldErrors, setFieldErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [department, setDepartment] = useState([]);
  const [orgCountryIso2, setOrgCountryIso2] = useState("GB");
  const [ready, setReady] = useState(false);

  const maxDate = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 5);
    return d;
  }, []);

  const visibleRoles = useMemo(
    () =>
      (roles || []).filter(
        (r) => !isRoleOptionHiddenForUserType(r?.label, true),
      ),
    [roles],
  );

  const fieldError = (key) =>
    fieldErrors?.[key] ? (
      <div className="user-form-field-error">{fieldErrors[key]}</div>
    ) : null;

  const loadCommonData = useCallback(async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return null;

    const [response, rolePermsResponse, orgResponse] = await Promise.all([
      api.post(API_ENDPOINTS.USER.GET_USER_PROFILE_COMMONDATA, {
        OrganizationId: userData.OrganizationId,
        StaffIDRef: userData.StaffId,
        CreatedBy: userData.StaffId,
        PageName: "UserProfile",
      }),
      api
        .post(API_ENDPOINTS.ROLES.StaffRolesActivity, {
          OrganizationId: userData.OrganizationId,
          CreatedBy: userData.StaffId,
          ActionType: "GetStaffRoles",
          Roles: "",
          RoleId: "",
        })
        .catch(() => null),
      fetchOrganizationSetup(userData.OrganizationId, userData.StaffId).catch(
        () => null,
      ),
    ]);

    let countryIso = "GB";
    if (orgResponse && Number(orgResponse?.success) === 1) {
      const { setupRow } = parseOrganizationSetupResponse(orgResponse);
      countryIso =
        String(setupRow?.CountryIso2 || "GB")
          .trim()
          .toUpperCase() || "GB";
      setOrgCountryIso2(countryIso);
    }

    if (Number(response?.success) !== 1) return null;

    const rolePermissionByName = new Map(
      (rolePermsResponse?.data?.SuccessStory || []).map((item) => [
        String(item.Roles || "").trim().toLowerCase(),
        item.Permission || "",
      ]),
    );
    const rolesData = (response.data.StaffRoleList || []).map((item) => ({
      label: item.Roles,
      value: item.RoleId,
      permission:
        item.Permission ||
        rolePermissionByName.get(
          String(item.Roles || "").trim().toLowerCase(),
        ) ||
        "",
    }));
    const departmentsData = utils.mapToOptions(
      response.data.DepartmentList,
      "DepartmentName",
      "DepartmentId",
    );

    setRoles(rolesData);
    setDepartment(departmentsData);

    // Always take EmpNum from this fresh load (same as internal Add User).
    const uniqueCodes = applyUniqueCodesFromEmpNum(
      response.data.EmpNum || response.data.EmpNumList || [],
    );

    return { uniqueCodes, countryIso };
  }, [userData]);

  const openForm = useCallback(async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) {
      showAlert?.({
        title: "Unable to open form",
        message: "Form options could not be loaded. Please try again.",
        type: "danger",
      });
      onClose?.();
      return;
    }

    try {
      showLoader("Loading form options...");
      const common = await loadCommonData();
      if (!common) {
        showAlert?.({
          title: "Unable to open form",
          message: "Form options could not be loaded. Please try again.",
          type: "danger",
        });
        onClose?.();
        return;
      }
      setFormData({
        ...emptyForm(common.countryIso),
        ...common.uniqueCodes,
        IsCRO: 1,
      });
      setFieldErrors({});
      setReady(true);
    } catch (err) {
      console.error("Sponsor/CRO add form open error:", err);
      showAlert?.({
        title: "Unable to open form",
        message: "Form options could not be loaded. Please try again.",
        type: "danger",
      });
      onClose?.();
    } finally {
      hideLoader();
    }
  }, [
    userData,
    loadCommonData,
    showAlert,
    onClose,
    showLoader,
    hideLoader,
  ]);

  React.useEffect(() => {
    if (!open) {
      setReady(false);
      return;
    }
    openForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once per open=true
  }, [open]);

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleClose = () => {
    setReady(false);
    setFormData(emptyForm(orgCountryIso2));
    setFieldErrors({});
    onClose?.();
  };

  const handleRoles = (role) => {
    const roleId =
      typeof role === "object" && role !== null ? role.value : role;
    const roleRecord = (roles || []).find(
      (item) =>
        String(item.value).toLowerCase() === String(roleId || "").toLowerCase(),
    );
    const roleLabel =
      (typeof role === "object" && role?.label) || roleRecord?.label || "";
    const rolePerms = isSystemAdministratorRole(roleLabel)
      ? filterAssignablePermissionKeys(getAllPermissionKeys())
      : expandStoredIsfPermissionKeys(
          expandStoredQmsPermissionKeys(
            filterAssignablePermissionKeys(
              getEffectivePermissionKeys(roleLabel, roleRecord?.permission),
            ),
          ),
        );

    clearFieldError("role");
    setFormData((prev) => ({
      ...prev,
      role: role || "",
      userPermissions: rolePerms,
    }));
  };

  const handleFormInput = (e) => {
    const { name, value, type, checked } = e.target || {};
    if (name) clearFieldError(name);
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const getRoleIdForValidation = () => {
    const role = formData.role;
    if (typeof role === "object" && role !== null) {
      return String(role.value ?? role.RoleId ?? "").trim();
    }
    return String(role ?? "").trim();
  };

  const getValidationErrorMap = () => {
    const errors = {};
    const trim = (value) => String(value ?? "").trim();

    if (!trim(formData.firstName)) errors.firstName = "First name is required.";
    if (!trim(formData.lastName)) errors.lastName = "Last name is required.";

    const mobileCountryIso2 = String(
      formData.mobileCountryIso2 || orgCountryIso2 || "GB",
    )
      .trim()
      .toUpperCase();
    const contactResult = validateNationalMobile(formData.contactNumber, {
      countryIso2: mobileCountryIso2,
      required: false,
    });
    if (!contactResult.valid) {
      errors.contactNumber = contactResult.message;
    }

    const empNo = trim(formData.employeeNumber);
    if (!empNo || empNo === "00000") {
      errors.employeeNumber = "Employee number is required.";
    }

    const email = trim(formData.email);
    if (!email) errors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Enter a valid email address.";
    }

    const confirm = trim(formData.confirmEmail);
    if (email && !confirm) {
      errors.confirmEmail = "Confirm email is required.";
    } else if (
      email &&
      confirm &&
      email.toLowerCase() !== confirm.toLowerCase()
    ) {
      errors.confirmEmail = "Email and confirm email must match.";
    }

    if (!getRoleIdForValidation()) errors.role = "Role is required.";

    return errors;
  };

  const handleSave = async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;

    const errorMap = getValidationErrorMap();
    if (Object.keys(errorMap).length > 0) {
      setFieldErrors(errorMap);
      return;
    }
    setFieldErrors({});

    try {
      showLoader("Saving user...");

      const response = await api.post(API_ENDPOINTS.USER.SAVE_UPDATE_USER, {
        StaffId: "",
        FirstName: formData.firstName || "",
        LastName: formData.lastName || "",
        EmployeeNum: formData.employeeNumber,
        EmployeeUniqueId: formData.employeeUniqueId || "",
        AscNum: formData.ascNum || "",
        AscNumId: formData.ascNumId || "",
        RoleIdRef: getRoleIdForValidation().toUpperCase() || "",
        Gender: formData.gender || "",
        StartDate: formData.startDate ? formatDate(formData.startDate) : "",
        AccessDate: formData.accessDate ? formatDate(formData.accessDate) : "",
        DOB: formData.dob ? formatDate(formData.dob) : "",
        EmailId: (formData.email || "").trim(),
        ContactNumber: (() => {
          const mobileCountryIso2 = String(
            formData.mobileCountryIso2 || orgCountryIso2 || "GB",
          )
            .trim()
            .toUpperCase();
          const result = validateNationalMobile(formData.contactNumber, {
            countryIso2: mobileCountryIso2,
            required: false,
          });
          return result.national || formData.contactNumber || "";
        })(),
        MobileCountryIso2: String(
          formData.mobileCountryIso2 || orgCountryIso2 || "GB",
        )
          .trim()
          .toUpperCase(),
        AddressLine1: formData.addressLine1 || "",
        City: formData.city || "",
        PostCode: formData.postalCode || "",
        County: formData.county || "",
        Country: formData.country || "",
        HaveLineManagerRights: formData.haveLineManagerRights ? 1 : 0,
        Action: "Insert",
        LineManagerId: formData.lineManager?.value || "",
        DepartmentId: formData.department?.value || "",
        StudyList: [],
        DocumentsXML: "",
        ExternalStaffISFDocuments: [],
        OrganizationId: userData?.OrganizationId.toUpperCase() || "",
        CreatedById: userData?.StaffId.toUpperCase() || "",
        LoggedInUserName: userData.UserName || "",
        OrganizationName: userData?.OrganizationName || "",
        Base64Image: formData.profilePicture || "",
        Extension: getFileExtensionFromBase64(formData.profilePicture),
        IsCRO: 1,
      });

      const procRows = extractSuccessStory(response);
      const procRow = procRows[0] ?? response?.data?.SuccessStory?.[0];
      const procOk =
        Number(response?.success) === 1 ||
        (procRow != null &&
          (Number(procRow.Success) === 1 || procRow.Success === true));
      const procFail =
        Number(response?.success) === 0 ||
        (procRow != null &&
          (Number(procRow.Success) === 0 || procRow.Success === false));

      if (procFail) {
        const failMessage =
          procRow?.MSG || response?.message || "Failed to save user.";
        if (/employee number already exists/i.test(failMessage)) {
          const refreshed = await loadCommonData();
          if (refreshed?.uniqueCodes) {
            setFormData((prev) => ({ ...prev, ...refreshed.uniqueCodes }));
            clearFieldError("employeeNumber");
          }
        }
        showAlert?.({
          title: "Error",
          message: failMessage,
          type: "danger",
        });
        return;
      }

      if (procOk || response?.success === 1) {
        const successMessage =
          procRow?.MSG ||
          response?.message ||
          "User has been saved successfully.";
        const savedStaffId = String(procRow?.StaffId || "").toUpperCase();
        showAlert?.({
          title: "Success",
          message: successMessage,
          type: "success",
        });
        handleClose();
        onSaved?.(savedStaffId);
      }
    } catch (err) {
      console.error("Sponsor/CRO add save error:", err);
      showAlert?.({
        title: "Error",
        message: "Failed to save user. Please try again.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  if (!open || !ready) return null;

  const dialIso = formData.mobileCountryIso2 || orgCountryIso2 || "GB";

  return (
    <div
      className="modal fade show d-block sponsor-cro-user-add-modal"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header" style={{ padding: "0.75rem 1rem" }}>
            <h5
              className="modal-title"
              style={{ fontSize: "1rem", margin: 0 }}
            >
              Add Sponsor / CRO User
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              aria-label="Close"
            />
          </div>

          <div className="modal-body sponsor-cro-user-add-modal__body">
            <div className="row scua-row--with-photo">
              <div className="col-md-9">
                <div className="row scua-row--validated">
                  <div className="col-md-4 scua-field">
                    <label>
                      First Name <span className="mandatory">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control${fieldErrors.firstName ? " is-invalid" : ""}`}
                      onChange={handleFormInput}
                      name="firstName"
                      value={formData.firstName || ""}
                    />
                    {fieldError("firstName")}
                  </div>
                  <div className="col-md-4 scua-field">
                    <label>
                      Last Name <span className="mandatory">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control${fieldErrors.lastName ? " is-invalid" : ""}`}
                      onChange={handleFormInput}
                      name="lastName"
                      value={formData.lastName || ""}
                    />
                    {fieldError("lastName")}
                  </div>
                  <div className="col-md-4 scua-field">
                    <label>Contact Number</label>
                    <div
                      className={`um-phone-input${fieldErrors.contactNumber ? " is-invalid" : ""}`}
                    >
                      <select
                        className="form-control um-phone-input__dial"
                        value={orgMobileDialLabel(dialIso)}
                        disabled
                        aria-label="Organisation country dial code"
                      >
                        <option value={orgMobileDialLabel(dialIso)}>
                          {orgMobileDialLabel(dialIso)}
                        </option>
                      </select>
                      <input
                        type="text"
                        className={`form-control${fieldErrors.contactNumber ? " is-invalid" : ""}`}
                        name="contactNumber"
                        value={formData.contactNumber || ""}
                        onChange={(e) => {
                          const { national, wrongDial } =
                            normalizeNationalMobileInput(
                              e.target.value,
                              dialIso,
                            );
                          setFormData((prev) => ({
                            ...prev,
                            contactNumber: national,
                          }));
                          setFieldErrors((prev) => {
                            const next = { ...prev };
                            if (wrongDial) {
                              next.contactNumber = `Contact number must use the organisation country dial code (${orgMobileDialLabel(dialIso)}).`;
                            } else {
                              delete next.contactNumber;
                            }
                            return next;
                          });
                        }}
                        onPaste={(e) => {
                          const text = e.clipboardData?.getData("text") ?? "";
                          e.preventDefault();
                          const { national, wrongDial } =
                            normalizeNationalMobileInput(text, dialIso);
                          setFormData((prev) => ({
                            ...prev,
                            contactNumber: national,
                          }));
                          setFieldErrors((prev) => {
                            const next = { ...prev };
                            if (wrongDial) {
                              next.contactNumber = `Contact number must use the organisation country dial code (${orgMobileDialLabel(dialIso)}).`;
                            } else {
                              delete next.contactNumber;
                            }
                            return next;
                          });
                        }}
                        inputMode="numeric"
                        autoComplete="tel-national"
                        placeholder="7123456789"
                      />
                    </div>
                    {fieldError("contactNumber")}
                  </div>
                </div>

                <div className="row scua-row--validated">
                  <div className="col-md-4 scua-field">
                    <label>
                      Employee Number <span className="mandatory">*</span>
                    </label>
                    <div className="span-under-input">
                      <span>{formData.prefix}</span>
                      <input
                        type="text"
                        className={`form-control${fieldErrors.employeeNumber ? " is-invalid" : ""}`}
                        name="employeeNumber"
                        value={formData.employeeNumber || ""}
                        onChange={handleFormInput}
                      />
                    </div>
                    {fieldError("employeeNumber")}
                  </div>
                  <div className="col-md-4 scua-field">
                    <label>
                      Email <span className="mandatory">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control${fieldErrors.email ? " is-invalid" : ""}`}
                      name="email"
                      value={formData.email || ""}
                      onChange={handleFormInput}
                    />
                    {fieldError("email")}
                  </div>
                  <div className="col-md-4 scua-field">
                    <label>
                      Confirm Email <span className="mandatory">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control${fieldErrors.confirmEmail ? " is-invalid" : ""}`}
                      name="confirmEmail"
                      value={formData.confirmEmail || ""}
                      onChange={handleFormInput}
                    />
                    {fieldError("confirmEmail")}
                  </div>
                </div>
              </div>
              <div className="col-md-3 scua-photo-col">
                <SimpleCircularUpload
                  currentImage={formData.profilePicture}
                  size={80}
                  maxFileSize={5}
                  onChange={(base64) => {
                    setFormData((prev) => ({
                      ...prev,
                      profilePicture: base64,
                    }));
                  }}
                />
              </div>
            </div>

            <div className="row scua-row--validated">
              <div className="col-md-3">
                <label>DOB</label>
                <DatePicker
                  selected={formData.dob}
                  onChange={(date) =>
                    setFormData((prev) => ({ ...prev, dob: date }))
                  }
                  placeholderText="Select date"
                  className="form-control"
                  dateFormat="dd/MM/yyyy"
                  isClearable
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  maxDate={new Date()}
                  onChangeRaw={(e) => e.preventDefault()}
                  popperProps={{ strategy: "fixed" }}
                />
              </div>
              <div className="col-md-3">
                <label>Start Date</label>
                <DatePicker
                  selected={formData.startDate}
                  onChange={(date) =>
                    setFormData((prev) => ({ ...prev, startDate: date }))
                  }
                  placeholderText="Select date"
                  className="form-control"
                  dateFormat="dd/MM/yyyy"
                  isClearable
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  minDate={new Date()}
                  maxDate={maxDate}
                  onChangeRaw={(e) => e.preventDefault()}
                  popperProps={{ strategy: "fixed" }}
                />
              </div>
              <div className="col-md-3">
                <label>Access Date</label>
                <DatePicker
                  selected={formData.accessDate}
                  onChange={(date) =>
                    setFormData((prev) => ({ ...prev, accessDate: date }))
                  }
                  placeholderText="Select date"
                  className="form-control"
                  dateFormat="dd/MM/yyyy"
                  isClearable
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  minDate={new Date()}
                  maxDate={maxDate}
                  onChangeRaw={(e) => e.preventDefault()}
                  popperProps={{ strategy: "fixed" }}
                />
              </div>
              <div className="col-md-3">
                <label>Gender</label>
                <GenderSelector
                  value={formData.gender}
                  onChange={(gender) =>
                    setFormData((prev) => ({
                      ...prev,
                      gender: gender || "",
                    }))
                  }
                />
              </div>
            </div>

            <div className="row scua-row--validated">
              <div className="col-md-3 scua-field">
                <label>
                  Role <span className="mandatory">*</span>
                </label>
                <Dropdown
                  options={visibleRoles}
                  isMulti={false}
                  selected={formData.role}
                  onSelect={handleRoles}
                />
                {fieldError("role")}
              </div>
              <div className="col-md-3">
                <label>Department</label>
                <Dropdown
                  options={department}
                  isMulti={false}
                  selected={formData.department}
                  onSelect={(departmentValue) =>
                    setFormData((prev) => ({
                      ...prev,
                      department: departmentValue || "",
                    }))
                  }
                />
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-md-12">
                <label>Address</label>
                <textarea
                  className="w-100 form-control"
                  name="addressLine1"
                  value={formData.addressLine1 || ""}
                  onChange={handleFormInput}
                  rows={2}
                />
              </div>
            </div>
            <div className="row mt-2">
              <div className="col-md-3">
                <label>Postal Code</label>
                <input
                  type="text"
                  className="form-control"
                  name="postalCode"
                  value={formData.postalCode || ""}
                  onChange={handleFormInput}
                />
              </div>
              <div className="col-md-3">
                <label>City</label>
                <input
                  type="text"
                  className="form-control"
                  name="city"
                  value={formData.city || ""}
                  onChange={handleFormInput}
                />
              </div>
              <div className="col-md-3">
                <label>County</label>
                <input
                  type="text"
                  className="form-control"
                  name="county"
                  value={formData.county || ""}
                  onChange={handleFormInput}
                />
              </div>
              <div className="col-md-3">
                <label>Country</label>
                <input
                  type="text"
                  className="form-control"
                  name="country"
                  value={formData.country || ""}
                  onChange={handleFormInput}
                />
              </div>
            </div>
          </div>

          <div className="modal-footer" style={{ padding: "0.75rem 1rem" }}>
            <div className="row">
              <div className="col-md-12">
                <button
                  type="button"
                  className="btn btn-outline-primary me-2"
                  onClick={handleClose}
                >
                  <i className="fas fa-close me-2" />
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  <i className="fas fa-plus me-2" />
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
